import React, { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Posts from '../data/data-post.json';
import { companyInfo, socialLinks } from '../data/companyInfo';
import useRemoteModule from '../../hooks/useRemoteModule';
import { mapBlogItem } from '../../services/contentApi';

function BlogDetailsMain() {
    const { slug } = useParams();
    const posts = useRemoteModule('blogs', mapBlogItem, Posts);

    const blogPost = useMemo(() => {
        if (!posts.length) {
            return null;
        }

        if (!slug) {
            return posts[0];
        }

        const bySlug = posts.find((post) => post.slug === slug);
        if (bySlug) {
            return bySlug;
        }

        const parsedId = parseInt(slug, 10);
        if (!Number.isNaN(parsedId)) {
            return posts.find((post) => post.id === parsedId) || posts[0];
        }

        return posts[0];
    }, [slug, posts]);

    if (!blogPost) {
        return null;
    }

    const recentPosts = posts.filter((post) => post.slug !== blogPost.slug).slice(0, 3);
    const categories = Object.entries(
        posts.reduce((accumulator, post) => {
            accumulator[post.category] = (accumulator[post.category] || 0) + 1;
            return accumulator;
        }, {})
    );

    return (
        <section className="th-blog-wrapper blog-details space-top space-extra-bottom">
            <div className="container shape-mockup-wrap">
                <div className="row">
                    <div className="col-xxl-8 col-lg-7">
                        <div className="th-blog blog-single">
                            <div className="blog-img">
                                <img src={`/assets/img/blog/${blogPost.bannerImg}`} alt={blogPost.title} />
                            </div>
                            <div className="blog-content">
                                <div className="blog-meta">
                                    <Link className="author" to="/blog">
                                        <i className="fa-light fa-user" />
                                        by {blogPost.author}
                                    </Link>
                                    <Link to="/blog">
                                        <i className="fa-regular fa-calendar" />
                                        {blogPost.publishDate}
                                    </Link>
                                    <Link to={`/blog/${blogPost.slug}`}>
                                        <img src="/assets/img/icon/map.svg" alt="" />
                                        {blogPost.location}
                                    </Link>
                                    <Link to={`/blog/${blogPost.slug}`}>
                                        <i className="fa-light fa-clock" />
                                        {blogPost.readTime}
                                    </Link>
                                </div>
                                <h2 className="blog-title">{blogPost.title}</h2>
                                {blogPost.intro.map((paragraph, index) => (
                                    <p className="blog-text mb-30" key={index}>
                                        {paragraph}
                                    </p>
                                ))}
                                <blockquote>
                                    <p>{blogPost.quote.text}</p>
                                    <cite>{blogPost.quote.author}</cite>
                                </blockquote>
                                {blogPost.sectionParagraphs.map((paragraph, index) => (
                                    <p className="blog-text mt-5 mb-4" key={index}>
                                        {paragraph}
                                    </p>
                                ))}
                                <h3 className="mt-4">{blogPost.sectionHeading}</h3>
                                <p>{blogPost.metaDescription}</p>
                                <div className="row gy-4">
                                    <div className="col-12">
                                        <div className="blog-img">
                                            <img
                                                className="w-100"
                                                src={`/assets/img/blog/${blogPost.inlineImage}`}
                                                alt={blogPost.title}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {blogPost.practicalTips.map((tip, index) => (
                                    <p key={index} className={index === 0 ? 'mb-0' : undefined}>
                                        {index + 1}. {tip}
                                    </p>
                                ))}
                                <div className="share-links clearfix">
                                    <div className="row justify-content-between">
                                        <div className="col-md-auto">
                                            <span className="share-links-title">Tags:</span>
                                            <div className="tagcloud">
                                                {blogPost.tags.map((tag) => (
                                                    <Link key={tag} to="/blog">
                                                        {tag}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="col-md-auto text-xl-end">
                                            <div className="share-links_wrapp">
                                                <span className="share-links-title">Share:</span>
                                                <div className="social-links">
                                                    <Link to={socialLinks.facebook} target="_blank" rel="noopener noreferrer">
                                                        <i className="fab fa-facebook-f" />
                                                    </Link>
                                                    <Link to={socialLinks.instagram} target="_blank" rel="noopener noreferrer">
                                                        <i className="fab fa-instagram" />
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="th-comment-form">
                            <div className="row">
                                <h3 className="blog-inner-title h4 mb-2">Need help planning a similar trip?</h3>
                                <p className="mb-25">
                                    Our team can help you choose the right route, season, and package for your travel goals.
                                </p>
                                <div className="col-12 form-group mb-0">
                                    <Link to="https://wa.me/03479020168?text=Assalam%20o%20Alaikum%21%20I%20want%20to%20book%20a%20tour%20with%20Himolan%20Peak%20Adventures." className="th-btn">
                                        Talk to Our Team
                                        <img src="/assets/img/icon/plane2.svg" alt="" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xxl-4 col-lg-5">
                        <aside className="sidebar-area">
                            <div className="widget widget_search">
                                <form className="search-form">
                                    <input type="text" placeholder="Search" required />
                                    <button type="submit">
                                        <i className="far fa-search" />
                                    </button>
                                </form>
                            </div>
                            <div className="widget widget_categories">
                                <h3 className="widget_title">Categories</h3>
                                <ul>
                                    {categories.map(([category, count]) => (
                                        <li key={category}>
                                            <Link to="/blog">
                                                <img src="/assets/img/theme-img/map.svg" alt="" />
                                                {category}
                                            </Link>
                                            <span>({count})</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="widget">
                                <h3 className="widget_title">Recent Posts</h3>
                                <div className="recent-post-wrap">
                                    {recentPosts.map((post) => (
                                        <div className="recent-post" key={post.id}>
                                            <div className="media-img">
                                                <Link to={`/blog/${post.slug}`}>
                                                    <img src={`/assets/img/blog/${post.image}`} alt={post.title} />
                                                </Link>
                                            </div>
                                            <div className="media-body">
                                                <h4 className="post-title">
                                                    <Link className="text-inherit" to={`/blog/${post.slug}`}>
                                                        {post.title}
                                                    </Link>
                                                </h4>
                                                <div className="recent-post-meta">
                                                    <Link to="/blog">
                                                        <i className="fa-regular fa-calendar" />
                                                        {post.publishDate}
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="widget widget_tag_cloud">
                                <h3 className="widget_title">Popular Tags</h3>
                                <div className="tagcloud">
                                    {blogPost.tags.map((tag) => (
                                        <Link key={tag} to="/blog">
                                            {tag}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                            <div
                                className="widget widget_offer"
                                style={{ background: 'url(/assets/img/bg/widget_bg_1.jpg)' }}
                            >
                                <div className="offer-banner">
                                    <div className="offer">
                                        <h6 className="box-title">Need Help? We Are Here To Help You</h6>
                                        <div className="banner-logo">
                                            <img src="/assets/img/primary-logo.png" alt="Himolan Peak Adventures" />
                                        </div>
                                        <div className="offer">
                                            <h6 className="offer-title">You Get Online support</h6>
                                            <Link className="offter-num" to={`tel:${companyInfo.phoneDial}`}>
                                                {companyInfo.phoneDisplay}
                                            </Link>
                                        </div>
                                        <Link to="https://wa.me/03479020168?text=Assalam%20o%20Alaikum%21%20I%20want%20to%20book%20a%20tour%20with%20Himolan%20Peak%20Adventures." className="th-btn style2 th-icon">
                                            Read More
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </aside>
                    </div>
                </div>
                <div className="shape-mockup shape1 d-none d-xxl-block" style={{ bottom: '5%', right: '-8%' }}>
                    <img src="/assets/img/shape/shape_1.png" alt="shape" />
                </div>
                <div className="shape-mockup shape2 d-none d-xl-block" style={{ bottom: '1%', right: '-7%' }}>
                    <img src="/assets/img/shape/shape_2.png" alt="shape" />
                </div>
                <div className="shape-mockup shape3 d-none d-xxl-block" style={{ bottom: '2%', right: '0%' }}>
                    <img src="/assets/img/shape/shape_3.png" alt="shape" />
                </div>
            </div>
        </section>
    );
}

export default BlogDetailsMain;


